"use client";

import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { SectionProductSelector } from "@/components/SectionProductSelector";
import { SectionOfferSelector } from "@/components/SectionOfferSelector";
import type {
  SectionItemOutput,
  SectionItemType,
} from "@/interfaces/section-items";
import type { ProductsOutput } from "@/interfaces/products";
import type { OffersOutput } from "@/interfaces/offers";
import { SectionItemCard } from "./SectionItemCard";

interface SectionItemsListProps {
  sectionId: string;
  items: SectionItemOutput[];
  onAddItem: (
    sectionId: string,
    itemId: string,
    itemType: SectionItemType,
    details?: ProductsOutput | OffersOutput
  ) => void;
  onToggleVisibility: (itemId: string) => void;
  onDelete: (itemId: string) => void;
  onMove: (itemId: string, direction: "up" | "down") => void;
}

export function SectionItemsList({
  sectionId,
  items,
  onAddItem,
  onToggleVisibility,
  onDelete,
  onMove,
}: SectionItemsListProps) {
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved =
      typeof window !== "undefined"
        ? localStorage.getItem(`section-expanded-${sectionId}`)
        : null;
    return saved !== null ? JSON.parse(saved) : true;
  });

  const toggleExpanded = useCallback(() => {
    const next = !isExpanded;
    setIsExpanded(next);
    if (typeof window !== "undefined") {
      localStorage.setItem(
        `section-expanded-${sectionId}`,
        JSON.stringify(next)
      );
    }
  }, [isExpanded, sectionId]);

  const sectionItems = useMemo(
    () =>
      items
        .filter((i) => i.sectionId === sectionId)
        .sort((a, b) => (a.position || 0) - (b.position || 0)),
    [items, sectionId]
  );

  return (
    <div className="mb-4">
      {/* Cabeçalho da seção */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <Button
          variant="ghost"
          onClick={toggleExpanded}
          className="flex items-center font-medium text-left hover:bg-transparent text-[#050505] p-0 h-auto gap-2 self-start"
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <span>Itens da Seção</span>
          <Badge
            variant="secondary"
            className="bg-[#EE7B11]/10 text-[#EE7B11] rounded-xl text-xs px-2 py-0.5 ml-2"
          >
            {sectionItems.length}
          </Badge>
        </Button>

        {/* Botões de adicionar - apenas no desktop, posicionados no topo */}
        {isExpanded && (
          <div className="hidden md:flex items-center gap-2">
            <SectionProductSelector
              onSelect={(p) => onAddItem(sectionId, p.id, "PRODUCT", p)}
              selectedProductIds={sectionItems
                .filter((i) => i.itemType === "PRODUCT")
                .map((i) => i.itemId)}
            />
            <SectionOfferSelector
              onSelect={(o) => onAddItem(sectionId, o.id, "OFFER", o)}
              selectedOfferIds={sectionItems
                .filter((i) => i.itemType === "OFFER")
                .map((i) => i.itemId)}
            />
          </div>
        )}
      </div>

      {/* Conteúdo */}
      {isExpanded && (
        <div className="mt-2">
          {sectionItems.length === 0 ? (
            <div className="text-center bg-[#D6D8CB]/20 border border-dashed border-gray-300 rounded-xl p-8">
              <div className="flex flex-col items-center gap-2">
                <div className="bg-gray-100 rounded-full p-3">
                  <ChevronRight className="h-6 w-6 text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium">
                  Nenhum item na seção
                </p>
                <p className="text-gray-400 text-sm">
                  <span className="hidden md:inline">
                    Use os botões acima para adicionar produtos ou ofertas
                  </span>
                  <span className="md:hidden">
                    Use os botões abaixo para adicionar produtos ou ofertas
                  </span>
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {sectionItems.map((item, index) => (
                <SectionItemCard
                  key={item.id}
                  item={item}
                  position={index + 1}
                  onToggleVisibility={onToggleVisibility}
                  onDelete={onDelete}
                  onMove={onMove}
                  canMoveUp={index > 0}
                  canMoveDown={index < sectionItems.length - 1}
                />
              ))}
            </div>
          )}

          {/* Botões de adicionar - apenas no mobile, posicionados no rodapé */}
          <div className="mt-4 md:hidden">
            <div className="rounded-xl border bg-[#F8F9FA] px-4 py-3">
              <div className="flex items-center gap-2 mb-3">
                <Plus className="h-4 w-4 text-[#EE7B11]" />
                <span className="text-sm font-medium text-[#050505]">
                  Adicionar à seção
                </span>
              </div>
              <div className="space-y-2">
                <SectionProductSelector
                  onSelect={(p) => onAddItem(sectionId, p.id, "PRODUCT", p)}
                  selectedProductIds={sectionItems
                    .filter((i) => i.itemType === "PRODUCT")
                    .map((i) => i.itemId)}
                />
                <SectionOfferSelector
                  onSelect={(o) => onAddItem(sectionId, o.id, "OFFER", o)}
                  selectedOfferIds={sectionItems
                    .filter((i) => i.itemType === "OFFER")
                    .map((i) => i.itemId)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
